/**
 * AYU Kompass – Gesundheitsüberblick (Redesign v2)
 * 
 * Überarbeitet:
 * - Positive Phänotyp-Beschreibungen (Stärken hervorheben)
 * - Ansprechenderes Layout mit Farbverläufen
 * - Empfehlungen als Karten statt Textliste
 * - Stimmungsverlauf als Grafik
 * - Aktivitätsübersicht kompakter
 */
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Heart,
  TrendingUp,
  BookOpen,
  MessageCircle,
  Calendar,
  Loader2,
  Info,
  Sparkles,
  Utensils,
  Dumbbell,
  Brain,
  Shield,
  Sun,
  Leaf,
  Star,
  ArrowRight,
} from "lucide-react";
import { PHENOTYPES, type PhenotypeResult, type PhenotypeId } from "@shared/phenotypeScoring";
import { useLocation } from "wouter";

/* ═══════════════════════════════════════════════════════════
   Positive Phänotyp-Beschreibungen (Stärken-basiert)
   ═══════════════════════════════════════════════════════════ */
const POSITIVE_PROFILES: Record<string, {
  tagline: string;
  strengths: string[];
  description: string;
  icon: string;
}> = {
  dry_light: {
    tagline: "Kreativ, beweglich, feinfühlig",
    strengths: ["Schnelle Auffassungsgabe", "Kreative Begabung", "Leichtigkeit im Denken", "Anpassungsfähig"],
    description: "Du bist ein feinfühliger Mensch mit einer besonderen kreativen Begabung. Dein wacher Geist erfasst Zusammenhänge schnell und deine natürliche Leichtigkeit macht dich flexibel und anpassungsfähig. Dein Körper spricht eine klare Sprache — wenn du auf ihn hörst, zeigt er dir genau, was er braucht.",
    icon: "🌬️",
  },
  sensitive_red: {
    tagline: "Zielstrebig, analytisch, leidenschaftlich",
    strengths: ["Starke Willenskraft", "Scharfer Verstand", "Natürliche Führungsstärke", "Leidenschaftlich"],
    description: "Du besitzt eine beeindruckende innere Kraft und Zielstrebigkeit. Dein analytischer Verstand und deine Leidenschaft treiben dich an, Großes zu erreichen. Deine Empfindsamkeit ist keine Schwäche — sie zeigt dir, wann es Zeit ist, einen Gang zurückzuschalten und deine Energie weise einzusetzen.",
    icon: "🔥",
  },
  oily_shiny: {
    tagline: "Vital, ausdauernd, genussvoll",
    strengths: ["Natürliche Vitalität", "Gute Regeneration", "Genussfähigkeit", "Kräftiges Haar & Haut"],
    description: "Du strahlst natürliche Vitalität aus. Dein Körper hat eine bemerkenswerte Fähigkeit zur Regeneration und Energiespeicherung. Deine Genussfähigkeit und dein Sinn für das Schöne machen dich zu einem Menschen, der das Leben in vollen Zügen genießen kann.",
    icon: "✨",
  },
  thin_nervous: {
    tagline: "Hochsensibel, intuitiv, schnell im Denken",
    strengths: ["Hohe Intuition", "Empathie", "Schnelles Denkvermögen", "Feinfühligkeit"],
    description: "Deine Hochsensibilität ist eine besondere Gabe. Du nimmst Nuancen wahr, die anderen entgehen, und deine Intuition ist bemerkenswert treffsicher. Dein schneller Geist verarbeitet Informationen blitzschnell. Wenn du lernst, deine Energie gezielt einzusetzen, entfaltest du dein volles Potenzial.",
    icon: "🦋",
  },
  cyclic_reactive: {
    tagline: "Rhythmisch, wandlungsfähig, tiefgründig",
    strengths: ["Natürliches Rhythmusgefühl", "Wandlungsfähigkeit", "Emotionale Tiefe", "Anpassungsfähig"],
    description: "Du lebst in natürlichen Zyklen und Rhythmen — und das ist eine Stärke. Deine Wandlungsfähigkeit erlaubt es dir, dich immer wieder neu zu erfinden. Deine emotionale Tiefe gibt dir ein reiches Innenleben und die Fähigkeit, andere Menschen auf einer tiefen Ebene zu verstehen.",
    icon: "🌙",
  },
  heavy_stable: {
    tagline: "Belastbar, ausdauernd, geerdet",
    strengths: ["Außergewöhnliche Belastbarkeit", "Innere Ruhe", "Ausdauer", "Verlässlichkeit"],
    description: "Du bist der Fels in der Brandung. Deine natürliche Belastbarkeit und innere Ruhe machen dich zu einem verlässlichen Anker für dich und andere. Deine Ausdauer ist beeindruckend — wenn du dich einmal für etwas entscheidest, ziehst du es durch. Deine Stärke liegt in deiner Beständigkeit.",
    icon: "🏔️",
  },
  stress_driven: {
    tagline: "Engagiert, leistungsstark, verantwortungsbewusst",
    strengths: ["Hohe Leistungsbereitschaft", "Verantwortungsbewusstsein", "Durchhaltevermögen", "Engagement"],
    description: "Dein Engagement und deine Leistungsbereitschaft sind bemerkenswert. Du übernimmst Verantwortung und gibst immer dein Bestes. Dein Körper zeigt dir durch seine Signale, dass er deine Fürsorge verdient. Wenn du lernst, bewusst Pausen einzubauen, wird deine ohnehin starke Leistung noch nachhaltiger.",
    icon: "⚡",
  },
  sensitive_allergic: {
    tagline: "Feinfühlig, achtsam, naturverbunden",
    strengths: ["Hohe Körperwahrnehmung", "Achtsamkeit", "Naturverbundenheit", "Sensibilität als Radar"],
    description: "Dein Körper hat ein besonders feines Radar. Was andere nicht bemerken, spürst du sofort — das ist eine wertvolle Fähigkeit. Deine natürliche Achtsamkeit und Naturverbundenheit zeigen dir den Weg zu dem, was dir wirklich guttut. Deine Sensibilität ist dein innerer Kompass.",
    icon: "🌿",
  },
  uv_sensitive: {
    tagline: "Aufmerksam, vorausschauend, bewusst",
    strengths: ["Vorausschauendes Denken", "Selbstfürsorge", "Bewusstes Leben", "Aufmerksamkeit für Details"],
    description: "Du gehst bewusst und vorausschauend mit dir und deinem Körper um. Deine Aufmerksamkeit für Details hilft dir, frühzeitig die richtigen Entscheidungen zu treffen. Dein bewusster Lebensstil ist eine Stärke, die dir langfristig zugutekommt.",
    icon: "☀️",
  },
  genetic_loaded: {
    tagline: "Wissend, proaktiv, präventiv stark",
    strengths: ["Proaktive Gesundheitsvorsorge", "Wissen als Stärke", "Bewusstes Handeln", "Resilienz"],
    description: "Du hast einen wertvollen Wissensvorsprung: Du kennst deine familiäre Geschichte und kannst aktiv gegensteuern. Dieses Wissen ist keine Last, sondern eine Chance. Wer seine Risiken kennt, kann gezielt vorbeugen — und genau das macht dich stark. Prävention ist deine Superkraft.",
    icon: "🧬",
  },
  digestive_sensitive: {
    tagline: "Körperbewusst, intuitiv essend, achtsam",
    strengths: ["Starkes Körperbewusstsein", "Intuitive Ernährung", "Achtsamkeit beim Essen", "Selbstkenntnis"],
    description: "Dein Verdauungssystem ist dein sensibelster Seismograph. Du spürst sofort, was dir guttut und was nicht — eine Fähigkeit, die viele Menschen erst mühsam erlernen müssen. Deine intuitive Verbindung zu deinem Körper ist eine echte Stärke. Wenn du auf dein Bauchgefühl hörst, liegst du meistens richtig.",
    icon: "🍃",
  },
  metabolic_sensitive: {
    tagline: "Strukturiert, diszipliniert, vorausplanend",
    strengths: ["Natürliche Disziplin", "Strukturiertes Denken", "Vorausplanung", "Körperintelligenz"],
    description: "Du hast eine natürliche Begabung für Struktur und Planung. Dein Körper reagiert besonders gut auf regelmäßige Rhythmen und bewusste Ernährung. Wenn du diese Stärke nutzt, wirst du feststellen, dass dein Stoffwechsel ein verlässlicher Partner ist, der auf deine Fürsorge mit Energie und Wohlbefinden antwortet.",
    icon: "⚖️",
  },
};

/* ═══════════════════════════════════════════════════════════ */

const MOOD_EMOJI: Record<string, string> = {
  "sehr gut": "😊",
  "gut": "🙂",
  "okay": "😐",
  "schlecht": "😔",
  "sehr schlecht": "😢",
  "energiegeladen": "⚡",
  "müde": "😴",
  "gestresst": "😤",
  "entspannt": "🧘",
  "glücklich": "😄",
};

export default function AppKompass() {
  const { client } = useClientAuth();
  const { t, locale } = useI18n();
  const [, navigate] = useLocation();

  const { data: stats, isLoading: statsLoading } = trpc.clientAuth.dashboardStats.useQuery(
    undefined,
    { staleTime: 60_000 }
  );

  const { data: moodHistory, isLoading: moodLoading } = trpc.clientAuth.moodHistory.useQuery(
    undefined,
    { staleTime: 60_000 }
  );

  const doshaProfile = client?.doshaProfile as PhenotypeResult | null;
  const primaryPhenotype = doshaProfile?.primary ? PHENOTYPES[doshaProfile.primary] : null;
  const secondaryPhenotype = doshaProfile?.secondary ? PHENOTYPES[doshaProfile.secondary] : null;
  const positiveProfile = doshaProfile?.primary ? POSITIVE_PROFILES[doshaProfile.primary] : null;

  const moodsWithEmoji = (moodHistory || [])
    .filter((m: { mood: string | null }) => m.mood)
    .slice(-14)
    .map((m: { mood: string | null; createdAt: Date }) => ({
      mood: m.mood!,
      emoji: MOOD_EMOJI[m.mood!.toLowerCase()] || "🙂",
      date: new Date(m.createdAt).toLocaleDateString(locale === "en" ? "en-GB" : "de-DE", { day: "2-digit", month: "2-digit" }),
    }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#2d3a1e]" style={{ fontFamily: "'Playfair Display', serif" }}>
          {t("kompass.title")}
        </h1>
        <p className="text-[#5a6b4a] mt-1">{t("kompass.subtitle")}</p>
      </div>

      {/* ═══ Phänotyp-Profil (Hero Card) ═══ */}
      {primaryPhenotype && positiveProfile ? (
        <div className="rounded-2xl overflow-hidden border border-[#d4dcc4]">
          {/* Hero Header */}
          <div
            className="px-6 py-8 text-center"
            style={{
              background: `linear-gradient(135deg, ${primaryPhenotype.color}18, ${primaryPhenotype.color}08)`,
            }}
          >
            <div className="text-4xl mb-3">{positiveProfile.icon}</div>
            <p className="text-xs text-[#8a9680] uppercase tracking-widest mb-2">{t("kompass.phenotyp")}</p>
            <h2
              className="text-2xl font-bold text-[#2d3a1e] mb-1"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {primaryPhenotype.name}
            </h2>
            <p className="text-sm text-[#6b7f4e] font-medium">{positiveProfile.tagline}</p>
            {secondaryPhenotype && (
              <p className="text-xs text-[#8a9680] mt-2">
                {t("kompass.mitTendenz")} <span className="font-medium text-[#5a6b4a]">{secondaryPhenotype.name}</span>
              </p>
            )}
          </div>

          {/* Positive Description */}
          <div className="px-6 py-5 bg-white border-t border-[#d4dcc4]">
            <p className="text-sm text-[#4a5e38] leading-relaxed">{positiveProfile.description}</p>
          </div>

          {/* Stärken */}
          <div className="px-6 py-5 bg-white border-t border-[#d4dcc4]">
            <p className="text-xs text-[#8a9680] uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Star className="w-3.5 h-3.5" />
              {t("kompass.staerken")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {positiveProfile.strengths.map((strength, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
                  style={{ background: `${primaryPhenotype.color}10` }}
                >
                  <div
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: primaryPhenotype.color }}
                  />
                  <span className="text-[#2d3a1e]">{strength}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Empfehlungen */}
          {primaryPhenotype.empfehlungen && (
            <div className="px-6 py-5 bg-white border-t border-[#d4dcc4]">
              <p className="text-xs text-[#8a9680] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Leaf className="w-3.5 h-3.5" />
                {t("kompass.typgerecht")}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-3 rounded-xl bg-[#f8f8f8] border border-[#e8e3d9]">
                  <div className="flex items-center gap-2 mb-2">
                    <Utensils className="w-4 h-4 text-[#6b7f4e]" />
                    <span className="text-xs font-medium text-[#2d3a1e]">{t("kompass.ernaehrung")}</span>
                  </div>
                  <p className="text-xs text-[#5a6b4a] leading-relaxed">{primaryPhenotype.empfehlungen.ernaehrung}</p>
                </div>
                <div className="p-3 rounded-xl bg-[#f8f8f8] border border-[#e8e3d9]">
                  <div className="flex items-center gap-2 mb-2">
                    <Dumbbell className="w-4 h-4 text-[#6b7f4e]" />
                    <span className="text-xs font-medium text-[#2d3a1e]">{t("kompass.bewegung")}</span>
                  </div>
                  <p className="text-xs text-[#5a6b4a] leading-relaxed">{primaryPhenotype.empfehlungen.bewegung}</p>
                </div>
                <div className="p-3 rounded-xl bg-[#f8f8f8] border border-[#e8e3d9]">
                  <div className="flex items-center gap-2 mb-2">
                    <Brain className="w-4 h-4 text-[#6b7f4e]" />
                    <span className="text-xs font-medium text-[#2d3a1e]">{t("kompass.mental")}</span>
                  </div>
                  <p className="text-xs text-[#5a6b4a] leading-relaxed">{primaryPhenotype.empfehlungen.mental}</p>
                </div>
              </div>
            </div>
          )}

          {/* Phänotyp-Verteilung (kompakt) */}
          {doshaProfile?.scores && Array.isArray(doshaProfile.scores) && (
            <div className="px-6 py-5 bg-white border-t border-[#d4dcc4]">
              <p className="text-xs text-[#8a9680] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5" />
                {t("kompass.verteilung")}
              </p>
              <div className="space-y-2">
                {(doshaProfile.scores as Array<{ phenotypeId: string; totalScore: number }>)
                  .sort((a, b) => b.totalScore - a.totalScore)
                  .slice(0, 5)
                  .map((entry) => {
                    const phenotype = PHENOTYPES[entry.phenotypeId as PhenotypeId];
                    const maxScore = Math.max(
                      ...(doshaProfile.scores as Array<{ totalScore: number }>).map((s) => s.totalScore)
                    );
                    const percentage = maxScore > 0 ? (entry.totalScore / maxScore) * 100 : 0;
                    const profile = POSITIVE_PROFILES[entry.phenotypeId];
                    return (
                      <div key={entry.phenotypeId} className="flex items-center gap-3">
                        <span className="text-sm w-5 flex-shrink-0">{profile?.icon || "•"}</span>
                        <span className="text-xs text-[#5a6b4a] w-32 truncate" title={phenotype?.name}>
                          {phenotype?.name || entry.phenotypeId}
                        </span>
                        <div className="flex-1 h-2.5 bg-[#f0ede5] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700 ease-out"
                            style={{
                              width: `${percentage}%`,
                              background:
                                entry.phenotypeId === doshaProfile.primary
                                  ? `linear-gradient(90deg, ${phenotype?.color || "#6b7f4e"}, ${phenotype?.color || "#6b7f4e"}cc)`
                                  : entry.phenotypeId === doshaProfile.secondary
                                    ? `${phenotype?.color || "#8a9680"}99`
                                    : "#c4bfb4",
                            }}
                          />
                        </div>
                        <span className="text-[10px] text-[#8a9680] w-8 text-right font-medium">
                          {Math.round(percentage)}%
                        </span>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* No phenotype yet — CTA */
        <Card className="border-[#d4dcc4] bg-gradient-to-br from-[#6b7f4e]/5 to-[#6b7c3e]/5">
          <CardContent className="py-8 text-center">
            <Sparkles className="w-10 h-10 text-[#6b7f4e]/30 mx-auto mb-3" />
            <h3
              className="text-lg font-semibold text-[#2d3a1e] mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("kompass.keinPhenotypTitle")}
            </h3>
            <p className="text-sm text-[#5a6b4a] max-w-md mx-auto leading-relaxed mb-4">
              {t("kompass.keinPhenotypDesc")}
            </p>
            <button
              onClick={() => navigate("/app/onboarding")}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-[#6b7f4e] text-white hover:bg-[#5a6d3e] transition-colors"
            >
              {t("kompass.fragebogenStarten")}
              <ArrowRight className="w-4 h-4" />
            </button>
          </CardContent>
        </Card>
      )}

      {/* ═══ Stimmungsverlauf ═══ */}
      <Card className="border-[#d4dcc4] bg-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-[#2d3a1e] text-base flex items-center gap-2">
            <Heart className="w-4 h-4 text-[#6b7f4e]" />
            {t("kompass.stimmungsverlauf")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {moodLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-[#6b7f4e]" />
            </div>
          ) : moodsWithEmoji.length === 0 ? (
            <div className="text-center py-6">
              <Heart className="w-8 h-8 text-[#6b7f4e]/15 mx-auto mb-2" />
              <p className="text-[#5a6b4a] text-sm">{t("kompass.keineStimmung")}</p>
              <p className="text-[#8a9680] text-xs mt-1">
                {t("kompass.stimmungHint")}
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {moodsWithEmoji.map((entry: { mood: string; emoji: string; date: string }, i: number) => (
                <div
                  key={i}
                  className="flex flex-col items-center gap-1 p-2.5 rounded-xl bg-[#f8f8f8] border border-[#e8e3d9] min-w-[56px] hover:border-[#d4dcc4] transition-colors"
                  title={`${entry.date}: ${entry.mood}`}
                >
                  <span className="text-lg">{entry.emoji}</span>
                  <span className="text-[9px] text-[#8a9680] font-medium">{entry.date}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═══ Aktivitätsübersicht ═══ */}
      <Card className="border-[#d4dcc4] bg-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-[#2d3a1e] text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#6b7f4e]" />
            {t("kompass.aktivitaet")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {statsLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-[#6b7f4e]" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-3 rounded-xl bg-[#f8f8f8] border border-[#e8e3d9] text-center">
                <BookOpen className="w-4 h-4 text-[#6b7c3e] mx-auto mb-1.5" />
                <p className="text-xl font-bold text-[#2d3a1e]">{stats?.diaryCount ?? 0}</p>
                <p className="text-[10px] text-[#8a9680]">{t("nav.tagebuch")}</p>
              </div>
              <div className="p-3 rounded-xl bg-[#f8f8f8] border border-[#e8e3d9] text-center">
                <MessageCircle className="w-4 h-4 text-[#6b7f4e] mx-auto mb-1.5" />
                <p className="text-xl font-bold text-[#2d3a1e]">{stats?.coachCount ?? 0}</p>
                <p className="text-[10px] text-[#8a9680]">{t("kompass.coach")}</p>
              </div>
              <div className="p-3 rounded-xl bg-[#f8f8f8] border border-[#e8e3d9] text-center">
                <Calendar className="w-4 h-4 text-[#8b6914] mx-auto mb-1.5" />
                <p className="text-xl font-bold text-[#2d3a1e]">{stats?.daysActive ?? 1}</p>
                <p className="text-[10px] text-[#8a9680]">{t("kompass.tageDabei")}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═══ Info-Hinweis ═══ */}
      <div className="flex items-start gap-3 p-4 rounded-xl bg-[#6b7f4e]/5 border border-[#d4dcc4]">
        <Info className="w-4 h-4 text-[#6b7f4e] mt-0.5 flex-shrink-0" />
        <p className="text-xs text-[#5a6b4a] leading-relaxed">
          {t("kompass.hinweis")}
        </p>
      </div>
    </div>
  );
}
